/**
 * Where you are.
 *
 * Tracked with `watchPosition`, not a single fix: on a phone the first reading
 * is a coarse network estimate that the GPS refines over the next few seconds,
 * and a marker that lands in the wrong block and stays there is worse than no
 * marker. The dot moves as the fix improves.
 *
 * There was an accuracy circle, drawn at its true radius in metres so that a
 * bad fix looked bad rather than looking confident. Honest, and unusable: a
 * desktop is located by network rather than GPS, which reports accuracy in
 * tens of kilometres, so the circle became a blue disc over the whole metro
 * area — the largest thing on the map, saying nothing anyone would act on.
 * The accuracy is still tracked, just not drawn.
 */

import { ScatterplotLayer, SolidPolygonLayer } from "@deck.gl/layers";

import { withinData } from "../lib/geo.js";
import { palette } from "../theme/palette.js";
import { OVER_EVERYTHING } from "../map/depth.js";

/** Radar sweep period. Slow enough to read as deliberate, not as a spinner. */
const SWEEP_MS = 2600;


/** Metres per degree of longitude at a latitude. */
const mPerDegLng = (lat) => 111320 * Math.cos((lat * Math.PI) / 180);

/**
 * A wedge pointing along a bearing, in metres.
 *
 * Sized in metres rather than pixels so it stays the same size on the ground as
 * you zoom, like everything else here that represents a real thing. Twelve
 * metres is long enough to read at street zoom and short enough not to look
 * like a route.
 */
export function headingWedge([lng, lat], heading, { length = 12, halfWidth = 4.5 } = {}) {
  const rad = (heading * Math.PI) / 180;
  const lngScale = mPerDegLng(lat);
  // Bearing is clockwise from north, so north is +lat and east is +lng.
  const point = (forward, side) => [
    lng + ((Math.sin(rad) * forward + Math.cos(rad) * side) / lngScale),
    lat + ((Math.cos(rad) * forward - Math.sin(rad) * side) / 110540),
  ];
  return [[
    point(length, 0),
    point(0, halfWidth),
    point(0, -halfWidth),
    point(length, 0),
  ]];
}

export class YouAreHere {
  constructor() {
    this.position = null;   // [lng, lat]
    this.accuracy = 0;      // metres
    /** Degrees clockwise from north, from the compass. Null until it reports. */
    this.heading = null;
    this.watchId = null;
    this.enabled = true;
  }

  /** Begin tracking. Silent if permission is refused — it is not an error. */
  start(onUpdate) {
    if (!navigator.geolocation || this.watchId !== null) return;

    this.watchId = navigator.geolocation.watchPosition(
      ({ coords }) => {
        const { longitude, latitude, accuracy } = coords;
        // Outside Denver there is no map under you, so there is nothing
        // useful to point at.
        if (!withinData(longitude, latitude)) return;
        this.position = [longitude, latitude];
        this.accuracy = Number.isFinite(accuracy) ? accuracy : 0;
        onUpdate?.(this.position, coords);
      },
      () => { /* refused, unavailable, or timed out — all mean "no dot" */ },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
  }

  stop() {
    if (this.watchId !== null) navigator.geolocation.clearWatch(this.watchId);
    this.watchId = null;
  }

  /**
   * `pulse` is the shared 0..1 phase, so the dot breathes with the beacons.
   *
   * Three expanding rings on top of a bright core. The earlier version was a
   * small dot with a faint halo and was genuinely hard to find on a dark map
   * among teal beacons and grey doors — which defeats the one job it has.
   */
  /**
   * What the dot is, on hover.
   *
   * Accuracy comes along because it is the one thing that changes what the dot
   * means: a fix good to 30m and one good to 12km draw identically, and the
   * halo that used to admit the difference was removed for being a blue disc
   * over the whole metro. Saying the number costs nothing and covers the ground
   * the halo was there for.
   */
  describe() {
    const rows = [];
    if (this.accuracy > 0) {
      rows.push(["Accuracy", this.accuracy < 1000
        ? `± ${Math.round(this.accuracy)} m`
        : `± ${(this.accuracy / 1000).toFixed(1)} km`]);
    }
    if (this.position) {
      rows.push(["Position",
        `${this.position[1].toFixed(5)}, ${this.position[0].toFixed(5)}`]);
    }
    rows.push(["Source", this.accuracy > 1000 ? "Network estimate" : "Device location"]);
    return { title: "Your Location", rows };
  }

  render(pulse = 0) {
    if (!this.enabled || !this.position) return [];

    const data = [{ p: this.position }];
    const sweep = (Date.now() % SWEEP_MS) / SWEEP_MS;

    const shared = {
      data,
      getPosition: (d) => d.p,
      radiusUnits: "meters",
      parameters: OVER_EVERYTHING,
      // The dot is the one thing on the map that is about the person looking
      // at it, and it was the one thing that would not answer to the cursor.
      pickable: true,
    };

    // Rings offset in phase so one is always mid-flight.
    const rings = [0, 0.33, 0.66].map((offset, i) => {
      const t = (sweep + offset) % 1;
      return new ScatterplotLayer({
        ...shared,
        id: `you-ring-${i}`,
        getRadius: 10 + t * 55,
        radiusMinPixels: 6,
        stroked: true,
        filled: false,
        lineWidthUnits: "pixels",
        getLineWidth: 2,
        getLineColor: [...palette().you, Math.round(200 * (1 - t) ** 1.4)],
        updateTriggers: { getRadius: t, getLineColor: t },
      });
    });

    return [
      // No accuracy halo.
      //
      // It was drawn at the real reported radius so that a bad fix looked bad.
      // The trouble is how bad the fix usually is: a desktop without GPS is
      // located by network, which reports accuracy in tens of kilometres, so
      // the halo became a blue disc swallowing the whole metro area. Zoomed
      // in you are inside it and never see the edge; zoomed out it is the
      // largest thing on the map, and it tells you nothing you would act on.
      // Which way you are facing.
      //
      // A dot says where you are and nothing about which way you are pointed,
      // which is the other half of the question anybody standing in a street is
      // asking. A wedge rather than a line: a line reads as ambiguous at both
      // ends, and a wedge has a nose.
      ...(Number.isFinite(this.heading) ? [new SolidPolygonLayer({
        id: "you-heading",
        data: [{ p: this.position }],
        getPolygon: (d) => headingWedge(d.p, this.heading),
        getFillColor: [...palette().you, 190],
        // The wedge is a direction, not a place, so it must not be occluded by
        // the buildings the direction points at.
        parameters: OVER_EVERYTHING,
        pickable: false,
        // Redrawn whenever the compass moves, which is the entire point.
        updateTriggers: { getPolygon: this.heading },
      })] : []),
      ...rings,
      // White collar, then the core: reads against both the dark ground and
      // the pale building tops.
      new ScatterplotLayer({
        ...shared,
        id: "you-collar",
        getRadius: 16,
        getFillColor: palette().youCollar,
        stroked: false,
        filled: true,
        radiusMinPixels: 8,
        radiusMaxPixels: 18,
      }),
      new ScatterplotLayer({
        ...shared,
        id: "you-dot",
        getRadius: 11,
        getFillColor: [...palette().you, 255],
        stroked: false,
        filled: true,
        radiusMinPixels: 5.5,
        radiusMaxPixels: 13,
      }),
    ];
  }
}
